import { Autocomplete, Button, Grid, InputLabel, OutlinedInput, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import { DbOrderManualTaskServiceInfos } from "../../../Core/Api/Types/db-order-manual-task-service-infos";
import { planningUserTaskService, userService } from "../../../Core/Api/services";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { produce } from "immer";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import moment from "moment";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../ModalConfirmAction/modal-confirm-action";
import { useTranslation } from "react-i18next";

export type ModalServiceManagerElementProps = {
  service?: DbOrderManualTaskServiceInfos;
  context: "Create" | "Update";
  onConfirm?: () => void;
};

export type ModalServiceManagerProps = CommonModalProps<ModalServiceManagerElementProps>;

const ModalServiceManager = React.forwardRef<any, ModalServiceManagerProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onConfirm, service, context } = props;
  const enqueueSnackbar = useCustomSnackbar();
  const isValid = useAppSelector(GetSectionIsValid("form-create-service", "sectionService"));
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [groups, setGroups] = useState<GroupAdvancedInfos[]>([]);
  const [newService, setNewService] = useState<DbOrderManualTaskServiceInfos>(
    service ? service : new DbOrderManualTaskServiceInfos({ createdDate: moment().toDate() })
  );

  useEffect(() => {
    // Get Groups
    userService.GetGroupsList().then((response) => {
      if (response.groups) {
        setGroups(response.groups);
      }
    });
  }, []);

  const handleCreate = () => {
    planningUserTaskService.CreateManualTasksService(newService).then((response) => {
      if (response.success) {
        enqueueSnackbar(t("alert:success.serviceCreated"), "success");
        onConfirm && onConfirm();
      } else enqueueSnackbar(response.message!, "error");
    });
    closeModal();
  };

  const handleUpdate = () => {
    planningUserTaskService.UpdateManualTasksService(newService).then((response) => {
      if (response.success) {
        enqueueSnackbar(t("alert:success.serviceUpdated"), "success");
        onConfirm && onConfirm();
      } else enqueueSnackbar(response.message!, "error");
    });

    closeModal();
  };

  const handleDeleteClick = () => {
    closeModal();

    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:body.deleteServiceConfirm"),
      title: t("modal:title.deleteService"),
      onConfirm: () => {
        planningUserTaskService.DeleteManualTasksService(newService.id).then((response) => {
          if (response.success) {
            enqueueSnackbar(t("alert:success.serviceDeleted"), "success");
          } else {
            enqueueSnackbar(response.message!, "error");
          }
        });
        onConfirm && onConfirm();
      },
    });
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.serviceManager")}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={handleCancelClick}>{t("common:close")}</Button>
          {context === "Create" && (
            <Button color="success" variant="outlined" onClick={handleCreate} disabled={!isValid}>
              {t("common:create")}
            </Button>
          )}
          {context === "Update" && (
            <>
              <Button color="success" variant="outlined" onClick={handleUpdate} disabled={!isValid}>
                {t("common:update")}
              </Button>
              <Button color="error" variant="outlined" onClick={handleDeleteClick}>
                {t("common:delete")}
              </Button>
            </>
          )}
        </Stack>
      }
    >
      <Typography variant="body2" color="text.tier" mb={2}>
        {context === "Create"
          ? t("modal:body.createNewService")
          : t("modal:body.updateExistingService", { serviceId: newService.id })}
      </Typography>

      <Grid container spacing={2} mb={2}>
        <Grid item md={6}>
          <FormInputText
            label={t("common:name")}
            value={newService?.name}
            onChange={(v) => {
              setNewService(
                produce((draft) => {
                  draft!.name = v.target.value;
                })
              );
            }}
            placeholder={t("common:name")}
            rows={3}
            validationProps={{
              formId: "form-create-service",
              formSectionId: "sectionService",
              fieldId: "name",
              initialState: false,
              required: true,
            }}
          />
        </Grid>
        <Grid item md={6}>
          <FormInputText
            label={t("common:description")}
            value={newService?.description}
            onChange={(v) => {
              setNewService(
                produce((draft) => {
                  draft!.description = v.target.value;
                })
              );
            }}
            placeholder={t("common:description")}
            rows={3}
            validationProps={{
              formId: "form-create-service",
              formSectionId: "sectionService",
              fieldId: "description",
              initialState: false,
              required: true,
            }}
          />
        </Grid>
      </Grid>
      <InputLabel sx={{ mb: 1 }}>{t("field:label.associateUserGroups")}</InputLabel>
      <Autocomplete
        options={groups}
        getOptionLabel={(option: any) => option.group.name}
        isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
        multiple
        value={groups.filter((x) => newService.groupIds?.includes(x.id))}
        onChange={(e, v) =>
          setNewService(
            produce((draft) => {
              draft!.groupIds = v.map((x) => x.id);
            })
          )
        }
        renderInput={(params) => (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            required
            sx={{ width: "100%" }}
            placeholder={t("field:placeholder.selectGroup")}
          />
        )}
      />
    </Modal>
  );
});

export default ModalServiceManager;
